import { PropsWithChildren } from "react";
import { Divider, Grid, Message, MessageContent, MessageHeader } from "semantic-ui-react";

import Property, { propertyUnits } from "../../rest/Property";

import Card from "../UI/Card/Card";
import Field from "./Field";

import "./DetailsCard.scss";
import EpcRatingTile from "../UI/EpcRatingTile/EpcRatingTile";

interface DetailsCardProps extends PropsWithChildren {
	property: Property;
}

const propertyColumns = [
	"wall_type",
	"wall_construction",
	"roof_type",
	"roof_construction",
	"glazing_type",
	"glazing_u_value",
	"fuel_type",
	"main_fuel_price",
	"lighting",
];

export default function DetailsCard({ children, property }: DetailsCardProps) {
	const formatPvString = (property: Property) => {
		let pvString = property.data.pvpower ? `${property.data.pvpower}kW h` : "None";
		if (property.pvOrientation != null) {
			pvString += ` (${property.pvOrientation})`;
		}
		return pvString;
	};

	const formatColumnValue = (column: string, default_?: string, nullZeroes = true): string => {
		const value = property[column as keyof Property] ?? property.data[column];

		if (nullZeroes) {
			// Zero values are taken as a missing value
			return value ? value + (propertyUnits[column] ?? "") : (default_ ?? "");
		}

		return value != null ? value + (propertyUnits[column] ?? "") : (default_ ?? "");
	};

	const formatMultipleColumnValues = (columns: string[], split?: "space" | "bracket"): string => {
		const colMerge = columns
			.reduce((acc, curr) => {
				if (split === "bracket" && acc.trim() !== "") {
					const formattedValue = formatColumnValue(curr);
					if (!formattedValue) return acc;

					return `${acc} (${formattedValue})`;
				}
				return `${acc} ${formatColumnValue(curr)}`;
			}, "")
			.trim();

		if (colMerge === "") {
			return "Not available";
		}
		return colMerge;
	};

	let rdsapFieldValue: string = "";

	if (property.data.sap_version === "2012") rdsapFieldValue = "n/a";
	else if (property.data.sap_is_rdsap) rdsapFieldValue = "✔";

	return (
		<Card title="Property Details" className="property-details">
			{property.isPropertyDataNull(propertyColumns) ? (
				<Message>
					<MessageHeader>No data</MessageHeader>
					<MessageContent>Could not find any data for this property</MessageContent>
				</Message>
			) : (
				<>
					<Grid>
						<Grid.Row>
							<Grid.Column>
								<Field label="UPRN" value={property.data.uprn} />

								<Field label="SAP Version" value={property.data.sap_version} />
								<Field label="RDSAP" value={rdsapFieldValue} />

								<Field label="Address" value={property.fullAddress} />
								<Field label="Property Type" value={property.propertyType} />
								<Field
									label="Wall Construction"
									value={formatMultipleColumnValues(["wall_type", "wall_construction"], "bracket")}
								/>
								<Field
									label="Roof Insulation"
									value={formatColumnValue("roof_type", undefined, false).concat(
										!!property.data.roof_construction
											? ` (${property.data.roof_construction}${propertyUnits["roof_construction"]})`
											: ""
									)}
								/>
								<Field label="Construction Age" value={formatColumnValue("construction_age")} />
								<Field
									label="Glazing"
									value={formatMultipleColumnValues(["glazing_type", "glazing_u_value"], "bracket")}
								/>
								<Field
									label="Fuel Type"
									value={formatMultipleColumnValues(["fuel_type", "main_fuel_price"], "bracket")}
								/>
								<Field
									label="Heating"
									value={formatMultipleColumnValues(["heatingType", "heating_efficiency"], "bracket")}
								/>
								<Field label="PV" value={formatPvString(property)} />
								<Field label="Lighting" value={formatColumnValue("lighting", "Not available")} />
							</Grid.Column>
						</Grid.Row>

						<Grid.Row>
							<Grid.Column>
								<Field label="Current Heat Demand" value={property.currentHeatDemand} />
								<Field label="Potential Heat Demand" value={property.potentialHeatDemand} />
								<Field label="Current CO2 Usage" value={property.currentCo2Usage} />
								<Field label="Potential CO2 Usage" value={property.potentialCo2Usage} />
								<Field label="Current EPC Lodgement Date" value={property.odcEpcLodgementDate} />
								<Field label="Last EPC Data Sync" value={property.odcLastAcceptedSuggestion} />
								<Field label="Last Data Update/Calculation" value={property.lastUpdated} />
							</Grid.Column>
						</Grid.Row>
					</Grid>
					<Divider vertical className="details-divide" />
				</>
			)}

			<div className="epc-ratings">
				<div className="rating current">
					<span className="label">Current</span>
					<span className="value">{property.epcRating ?? "N/A"}</span>
				</div>
				<div className="rating potential">
					<span className="label">Potential</span>
					<span className="value">{Number(property.potentialEpcRating)?.toFixed(0) ?? "N/A"}</span>
				</div>
				{property.epcRating && (
					<div className="rating grade">
						<span className="label">Grade</span>
						<EpcRatingTile rating={property.epcRating} />
					</div>
				)}
				{children}
			</div>
		</Card>
	);
}
